// Sales report: monthly Gross Sales for the landlord (Exhibit G) and the
// GM check. Read from Stripe by the backend (#290). Admins and holders of
// the "sales_report" permission only (the server checks; the menu hides it
// from everyone else).
//
// Rule (owner-approved): captured charges in the month of the charge, minus
// refunds in the month of the refund. Disputes are shown, not subtracted.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import StaffLayout from "./StaffLayout";
import { salesReportAPI } from "../../services/api";
import "./SalesReportPage.css";

const FIRST_YEAR_FALLBACK = 2025;

const SOURCE_LABELS = [
  ["pos_reader", "POS reader"],
  ["online_checkout", "Online (Checkout)"],
  ["invoice", "Invoice / subscription"],
  ["saved_card", "Saved card"],
  ["other", "Other"],
];

// The statement text on the Exhibit G PDF. Order and labels for the form.
const SETTINGS_FIELDS = [
  { key: "titleEntity", label: "Company name (title)" },
  { key: "leaseParagraph", label: "Lease paragraph", long: true },
  { key: "center", label: "Center" },
  { key: "storeNameNumber", label: "Store name / number", long: true },
  { key: "submittedTo", label: "Submitted to", long: true },
  { key: "openingDate", label: "Opening date", type: "date" },
  { key: "openingNote", label: "Opening note", long: true },
  {
    key: "nextStatementTemplate",
    label: "Next statement line (year in progress)",
    long: true,
    hint: "{month}, {year} and {dueDate} are filled in.",
  },
  {
    key: "remainingMonthsTemplate",
    label: "Later months line (year in progress)",
    long: true,
    hint: "{months} is filled in, for example \"November and December 2026\".",
  },
  { key: "statementDueDay", label: "Statement due day (of the next month)", type: "number" },
  { key: "certificationText", label: "Certification", long: true },
  { key: "signerName", label: "Signer name" },
  { key: "signerTitle", label: "Signer title" },
];

const money = (cents) =>
  cents === null || cents === undefined
    ? ""
    : (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const readBlobText = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

// With responseType "blob", an error body arrives as a Blob. Read its JSON.
async function errorMessage(error, fallback) {
  const response = error?.response;
  if (!response) return fallback;
  let data = response.data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      data = JSON.parse(await readBlobText(data));
    } catch {
      data = null;
    }
  }
  if (response.status === 403) {
    return "You do not have access to the sales report. Ask an admin.";
  }
  return data?.message || fallback;
}

function saveFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function filenameFrom(response, fallback) {
  const header = response?.headers?.["content-disposition"] || "";
  const match = /filename="([^"]+)"/.exec(header);
  return match ? match[1] : fallback;
}

// Refunds come off, so they show with a minus sign (but never "−$0.00").
const minus = (cents) => (cents ? `−${money(cents)}` : money(0));

const sumClosed = (months, field) =>
  months.filter((m) => m.state === "closed").reduce((total, m) => total + (m[field] || 0), 0);

function YearTable({ yearReport }) {
  const { year, months, totalGrossSales, complete } = yearReport;
  const closed = months.filter((m) => m.state === "closed");
  const taxCalculated = closed.some((m) => m.salesTaxCalculatedCharges > 0);
  const sourceTotal = (key) =>
    closed.reduce((total, m) => total + ((m.bySource && m.bySource[key]) || 0), 0);

  return (
    <section className="sales-report-year" data-testid={`sales-report-year-${year}`}>
      <h2>
        {year}
        {!complete && <span className="sales-report-badge">Year to date</span>}
      </h2>
      <div className="sales-report-table-wrap">
        <table className="sales-report-table">
          <thead>
            <tr>
              <th scope="col">Month</th>
              <th scope="col">Charges</th>
              <th scope="col">Refunds</th>
              <th scope="col">Gross Sales</th>
              <th scope="col">Sales tax{taxCalculated ? " *" : ""}</th>
              <th scope="col">Disputes (not subtracted)</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.key} className={`sales-report-row-${m.state}`}>
                <th scope="row">{m.label}</th>
                {m.state === "closed" ? (
                  <>
                    <td>{money(m.charges)}</td>
                    <td>{minus(m.refunds)}</td>
                    <td className="sales-report-gross">{money(m.grossSales)}</td>
                    <td>
                      {money(m.salesTax)}
                      {m.salesTaxCalculatedCharges > 0 ? " *" : ""}
                    </td>
                    <td>
                      {m.disputeCount > 0
                        ? `${money(m.disputes)} (${m.disputeCount})`
                        : "—"}
                    </td>
                  </>
                ) : (
                  <td colSpan={5} className="sales-report-muted">
                    {m.state === "not_open" ? "Not open" : "Month not closed yet"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total</th>
              <td>{money(sumClosed(months, "charges"))}</td>
              <td>{minus(sumClosed(months, "refunds"))}</td>
              <td className="sales-report-gross" data-testid={`sales-report-total-${year}`}>
                {money(totalGrossSales)}
              </td>
              <td>{money(sumClosed(months, "salesTax"))}</td>
              <td>{money(sumClosed(months, "disputes"))}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {taxCalculated && (
        <p className="sales-report-note">
          * Some charges had no tax line in Stripe. Their tax is calculated at 8.6%.
        </p>
      )}
      {closed.length > 0 && (
        <details className="sales-report-sources">
          <summary>Where the money came from ({year})</summary>
          <div className="sales-report-table-wrap">
            <table className="sales-report-table">
              <thead>
                <tr>
                  <th scope="col">Month</th>
                  {SOURCE_LABELS.map(([key, label]) => (
                    <th scope="col" key={key}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {closed.map((m) => (
                  <tr key={m.key}>
                    <th scope="row">{m.label}</th>
                    {SOURCE_LABELS.map(([key]) => (
                      <td key={key}>{money((m.bySource && m.bySource[key]) || 0)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {SOURCE_LABELS.map(([key]) => (
                    <td key={key}>{money(sourceTotal(key))}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="sales-report-note">
            These are charges before refunds, by where the payment was taken.
          </p>
        </details>
      )}
    </section>
  );
}

function SettingsForm({ settings, onSaved }) {
  const [values, setValues] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => setValues(settings), [settings]);

  const changed = SETTINGS_FIELDS.filter(
    ({ key }) => String(values[key] ?? "") !== String(settings[key] ?? "")
  );

  const save = async (event) => {
    event.preventDefault();
    if (changed.length === 0) return;
    setSaving(true);
    setMessage(null);
    try {
      const update = Object.fromEntries(changed.map(({ key }) => [key, values[key]]));
      const response = await salesReportAPI.updateSettings(update);
      onSaved(response.data);
      setMessage({ type: "success", text: "Saved. The next PDF uses the new text." });
    } catch (error) {
      setMessage({ type: "error", text: await errorMessage(error, "Could not save the text.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="sales-report-settings" onSubmit={save} aria-label="Statement text">
      {SETTINGS_FIELDS.map(({ key, label, long, type, hint }) => (
        <div className="sales-report-field" key={key}>
          <label htmlFor={`sales-report-${key}`}>{label}</label>
          {long ? (
            <textarea
              id={`sales-report-${key}`}
              rows={key === "leaseParagraph" ? 4 : 2}
              value={values[key] ?? ""}
              onChange={(e) => setValues({ ...values, [key]: e.target.value })}
            />
          ) : (
            <input
              id={`sales-report-${key}`}
              type={type || "text"}
              min={type === "number" ? 1 : undefined}
              max={type === "number" ? 28 : undefined}
              value={values[key] ?? ""}
              onChange={(e) => setValues({ ...values, [key]: e.target.value })}
            />
          )}
          {hint && <small>{hint}</small>}
        </div>
      ))}
      {message && (
        <p
          className={`sales-report-message sales-report-message-${message.type}`}
          role={message.type === "error" ? "alert" : "status"}
        >
          {message.text}
        </p>
      )}
      <div className="sales-report-actions">
        <button type="submit" className="sales-report-button" disabled={saving || changed.length === 0}>
          {saving ? "Saving…" : "Save text"}
        </button>
        <button
          type="button"
          className="sales-report-button sales-report-button-secondary"
          disabled={saving || changed.length === 0}
          onClick={() => {
            setValues(settings);
            setMessage(null);
          }}
        >
          Undo changes
        </button>
      </div>
    </form>
  );
}

const SalesReportPage = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);
  const [selectedYears, setSelectedYears] = useState([currentYear]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const firstYear = useMemo(() => {
    const year = Number(String(settings?.openingDate || "").slice(0, 4));
    return year >= 2020 && year <= currentYear ? year : Math.min(FIRST_YEAR_FALLBACK, currentYear);
  }, [settings, currentYear]);
  const yearChoices = [];
  for (let y = firstYear; y <= currentYear; y += 1) yearChoices.push(y);

  const loadReport = useCallback(async (years) => {
    setLoading(true);
    setError(null);
    try {
      const response = await salesReportAPI.getReport(years);
      setReport(response.data);
    } catch (err) {
      setReport(null);
      setError(await errorMessage(err, "Could not build the sales report."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    salesReportAPI
      .getSettings()
      .then((response) => setSettings(response.data))
      .catch(() => setSettings(null));
    loadReport([currentYear]);
  }, [loadReport, currentYear]);

  const toggleYear = (year) =>
    setSelectedYears((years) =>
      years.includes(year) ? years.filter((y) => y !== year) : [...years, year].sort()
    );

  const download = async (kind) => {
    const years = [...selectedYears].sort();
    setDownloading(kind);
    setDownloadError(null);
    try {
      const response =
        kind === "pdf"
          ? await salesReportAPI.downloadExhibitG(years)
          : await salesReportAPI.downloadCheckCsv(years);
      const fallback =
        kind === "pdf"
          ? `Exhibit-G-Gross-Sales-${years.join("-")}.pdf`
          : `Gross-Sales-Check-${years.join("-")}.csv`;
      saveFile(response.data, filenameFrom(response, fallback));
    } catch (err) {
      setDownloadError(await errorMessage(err, "Could not build the file."));
    } finally {
      setDownloading(null);
    }
  };

  const noYears = selectedYears.length === 0;
  const otherCharges = report?.otherCharges || [];

  return (
    <StaffLayout>
      <div className="sales-report-page">
        <h1>Sales Report</h1>
        <p className="sales-report-intro">
          Monthly Gross Sales for the landlord (Exhibit G), read from Stripe. A charge counts in the
          month it was made. A refund comes off in the month it was made. Disputes are shown but
          not subtracted. A month shows once it has closed.
        </p>

        <div className="sales-report-controls">
          <fieldset className="sales-report-years">
            <legend>Years</legend>
            {yearChoices.map((year) => (
              <label key={year} className="sales-report-year-choice">
                <input
                  type="checkbox"
                  checked={selectedYears.includes(year)}
                  onChange={() => toggleYear(year)}
                />
                {year}
              </label>
            ))}
          </fieldset>
          <div className="sales-report-actions">
            <button
              type="button"
              className="sales-report-button"
              disabled={noYears || loading}
              onClick={() => loadReport([...selectedYears].sort())}
            >
              {loading ? "Reading Stripe…" : "Show report"}
            </button>
            <button
              type="button"
              className="sales-report-button sales-report-button-secondary"
              disabled={noYears || downloading !== null}
              onClick={() => download("pdf")}
            >
              {downloading === "pdf" ? "Building PDF…" : "Download Exhibit G (PDF)"}
            </button>
            <button
              type="button"
              className="sales-report-button sales-report-button-secondary"
              disabled={noYears || downloading !== null}
              onClick={() => download("csv")}
            >
              {downloading === "csv" ? "Building CSV…" : "Download GM check (CSV)"}
            </button>
          </div>
          {noYears && <p className="sales-report-note">Choose at least one year.</p>}
          {downloadError && (
            <p className="sales-report-message sales-report-message-error" role="alert">
              {downloadError}
            </p>
          )}
        </div>

        {loading && (
          <p className="sales-report-note" role="status">
            Reading Stripe. This can take up to a minute for more than one year.
          </p>
        )}
        {error && (
          <p className="sales-report-message sales-report-message-error" role="alert">
            {error}
          </p>
        )}

        {report && !loading && (
          <>
            {report.years.map((yearReport) => (
              <YearTable key={yearReport.year} yearReport={yearReport} />
            ))}
            {otherCharges.length > 0 && (
              <details className="sales-report-sources">
                <summary>
                  {otherCharges.length} charge{otherCharges.length === 1 ? "" : "s"} with source
                  "Other" (counted in the totals)
                </summary>
                <ul className="sales-report-other">
                  {otherCharges.map((c) => (
                    <li key={c.id}>
                      {new Date(c.created * 1000).toLocaleDateString("en-US", {
                        timeZone: "America/Phoenix",
                      })}{" "}
                      — {money(c.amount)} — <code>{c.id}</code>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <p className="sales-report-note">
              Read from Stripe at{" "}
              {new Date(report.generatedAt).toLocaleString("en-US", {
                timeZone: "America/Phoenix",
              })}{" "}
              (Arizona time).
            </p>
          </>
        )}

        {settings && (
          <details className="sales-report-settings-wrap">
            <summary>Statement text on the Exhibit G PDF</summary>
            <SettingsForm settings={settings} onSaved={setSettings} />
          </details>
        )}
      </div>
    </StaffLayout>
  );
};

export default SalesReportPage;
